import React from "react";
import { useNavigate } from "react-router-dom";

import { useAccountState } from "../hooks/useAccountState";
import type { FeatureKey } from "../lib/api";
import {
  canUseFeature,
  FEATURE_LOCK_MESSAGE,
  resolveFeatureAccessUiState,
  type FeatureAccessUiState,
} from "../lib/featureAccess";

type RequestCardProps = {
  title: string;
  description: string;
  accessState?: FeatureAccessUiState;
  trialBadge?: string;
  onClick?: () => void;
  onLockedClick?: () => void;
};

function RequestCard({
  title,
  description,
  accessState = "available",
  trialBadge,
  onClick,
  onLockedClick,
}: RequestCardProps) {
  const isLocked = accessState === "locked";
  const isChecking = accessState === "checking";
  return (
    <button
      type="button"
      aria-label={title}
      onClick={isLocked ? onLockedClick : isChecking ? undefined : onClick}
      aria-disabled={isLocked || isChecking ? "true" : undefined}
      aria-busy={isChecking ? "true" : undefined}
      disabled={isChecking}
      className={`h-full w-full rounded-[1.8rem] border p-5 text-left shadow-[0_12px_28px_rgba(124,90,78,0.10)] transition ${
        isLocked
          ? "border-[#E0D5CE] bg-[#F4EEE9] opacity-85"
          : `border-[#EADBC8] bg-white ${isChecking ? "cursor-default" : "hover:-translate-y-0.5 hover:bg-[#FFFCFA]"}`
      }`}
    >
      {isLocked ? (
        <span className="inline-flex rounded-full bg-white px-3 py-1 text-[11px] font-black text-[#A1887D]">
          ロック中
        </span>
      ) : trialBadge ? (
        <span className="inline-flex rounded-full bg-[#FFF3E6] px-3 py-1 text-[11px] font-black text-[#A67C52]">
          {trialBadge}
        </span>
      ) : null}
      <h3 className={`${isLocked || trialBadge ? "mt-4" : ""} text-lg font-black text-[#5D4D46]`}>{title}</h3>
      <p className="mt-2 text-sm leading-6 text-[#6B5851]">{description}</p>
      <p className="mt-4 text-sm font-bold text-[#A67C52]">
        {isLocked ? FEATURE_LOCK_MESSAGE : "開く →"}
      </p>
    </button>
  );
}

export default function RequestEntry() {
  const navigate = useNavigate();
  const { accountState, loading: accountStateLoading, error: accountStateError } = useAccountState();
  const [lockedMessage, setLockedMessage] = React.useState<string | null>(null);

  const getAccessState = (feature: FeatureKey) => resolveFeatureAccessUiState(
    accountState,
    accountStateLoading,
    canUseFeature(accountState, feature)
  );
  const showLockedMessage = () => setLockedMessage(FEATURE_LOCK_MESSAGE);

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-md">
        <section className="rounded-[2.4rem] border border-white/80 bg-white/90 p-5 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-7">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-[#A67C52]">RESEARCH HUB</p>
            <h1 className="mt-3 text-3xl font-black tracking-tight text-[#7C5A4E]">調べてもらう</h1>
            <p className="mt-3 text-sm leading-7 text-[#6B5851]">
              企業やニュース、気になるテーマの調査をメイに任せられます。
            </p>
          </div>

          <div className="mt-8 space-y-8">
            {lockedMessage ? (
              <p role="status" className="rounded-xl bg-[#FFF8F1] px-4 py-3 text-sm font-bold text-[#A67C52]">
                {lockedMessage}
              </p>
            ) : null}
            {accountStateError ? (
              <p role="alert" className="rounded-xl bg-[#FFF8F1] px-4 py-3 text-sm font-bold text-[#A67C52]">
                利用状態を確認できませんでした。もう一度お試しください。
              </p>
            ) : null}

            <section aria-labelledby="request-research-heading">
              <h2 id="request-research-heading" className="text-base font-black text-[#5D4D46]">
                調査する
              </h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <RequestCard
                  title="企業リサーチ"
                  description="商談先・提携先などの公開情報を詳しく調べる。"
                  accessState={getAccessState("company_research")}
                  onLockedClick={showLockedMessage}
                  onClick={() => navigate("/request/company-research", { state: { startNew: true } })}
                />
                <RequestCard
                  title="ニュース"
                  description="気になるテーマの最新情報を調べる・受け取る。"
                  accessState={getAccessState("news")}
                  onLockedClick={showLockedMessage}
                  onClick={() => navigate("/request/news")}
                />
                <RequestCard
                  title="深掘りレポート"
                  description="論点ごとに根拠と出典をたどれる形式でテーマを深く調べる。"
                  accessState={getAccessState("report")}
                  onLockedClick={showLockedMessage}
                  onClick={() => navigate("/request/deep-report")}
                />
              </div>
            </section>

          </div>
        </section>
      </div>
    </main>
  );
}
